/**
 * Os motivos pelos quais uma porta de duelo recusa a entrada.
 * Fonte da verdade: docs/18 secao 35.3, docs/09 secao 2.1 e 2.3, docs/14 secao 5.
 *
 * ## Por que isto e contrato, e nao uniao local de cada servico
 *
 * Estes codigos atravessam DOIS processos: o `apps/api` os produz, e o `apps/web2` escolhe o texto
 * e a acao da tela a partir deles (docs/18 secao 35.4). Ate esta lista existir, cada lado escrevia
 * a sua — uniao de strings no servico, `switch` de strings na tela — e **nenhum compilador via as
 * duas**.
 *
 * O resultado esta medido: sete mapas de motivo para texto no `apps/web2`, nenhum completo, quatro
 * redacoes diferentes para `NO_DECK`, e um mapa anunciado como exaustivo que cobria tres dos oito
 * motivos da fila. Um mapa exaustivo sobre uma lista copiada a mao e exaustivo sobre a copia.
 *
 * Com a lista aqui, a cadeia tem tres elos e cada um quebra sozinho:
 *
 * 1. Servico que devolve um codigo fora da lista nao compila no `apps/api`.
 * 2. Codigo novo na lista sem texto nao compila no i18n do `apps/web2`.
 * 3. Codigo novo na lista sem decisao de acao nao compila no dialogo de recusa.
 *
 * ## O que NAO entra aqui
 *
 * Erro de loja, de mercado, de carrinho ou de colecao. Esta lista e sobre **portas de duelo**
 * (docs/18 secao 35.1), e nao um repositorio de todos os codigos do jogo — uma lista que aceitasse
 * qualquer codigo pararia de quebrar o build por omissao, que e a unica coisa que ela faz.
 */

/**
 * Os motivos. A ordem e a de docs/09 secao 2.1: manutencao, depois o que depende do corpo do
 * pedido, depois o que depende do jogador, depois o que depende do alvo ou do modo — e por ultimo
 * os que nascem no cliente.
 *
 * Ela nao e a ordem em que qualquer servico confere, e nao pode ser lida como tal: cada porta
 * confere o subconjunto dela, na ordem que docs/09 fixa para aquela porta.
 */
export const DUEL_GATE_REFUSALS = [
  /**
   * A janela de atualizacao esta valendo (docs/09 secao 2.3). Primeiro da lista porque e a unica
   * recusa que nao depende de nada do jogador, e por isso vem antes de todas nas oito portas.
   */
  'MAINTENANCE',

  // O que o corpo do pedido invalida, antes de qualquer consulta ao banco.
  'INVALID_TERRAIN_MODE',
  'INVALID_NICKNAME',
  'SELF_INVITE',
  'INVALID_MAX_PLAYERS',
  'INVALID_JOIN_CODE',

  // O que o estado do jogador recusa.
  'ACCOUNT_FROZEN',
  'INCOMPLETE_PROFILE',
  /**
   * O jogador novo ainda nao concluiu o capitulo obrigatorio do Duelo Guiado (docs/20 secao 7.2, ADR 0059). Vem logo
   * depois de `INCOMPLETE_PROFILE` e antes do deck (docs/09 secao 2.1). Vale tambem nas portas da economia.
   */
  'GUIDED_REQUIRED',
  'ALREADY_QUEUED',
  'ALREADY_INVITING',
  'BUSY_IN_DUEL',
  'IN_CHAMPIONSHIP',
  /** O jogador esta no mundo do modo historia, que e exclusivo como o campeonato (docs/09 secao 1.1). */
  'IN_STORY_MODE',
  'ALREADY_IN_CHAMPIONSHIP',
  /**
   * Declarado e **nunca emitido**: a porta do campeonato preve o caso e nao consulta a fila
   * (`ChampionshipService.assertEligible`). Fica na lista porque ela e sobre o que o servidor PODE
   * dizer, e o dia em que aquele `if` nascer nao pode ser o dia em que a tela fica sem texto.
   * Registrado em docs/backlog/66-a-recusa-que-o-jogador-nunca-via.md.
   */
  'BUSY_IN_QUEUE',

  // O deck.
  'NO_DECK',
  'DECK_INVALID',

  // O saldo. Star Chips na fila, no Duelo de Lenda e na Trilha e Corrida premiadas quando cobram
  // (docs/06 sec 2 e 2.2), Moedas na entrada de campeonato (docs/06 sec 16).
  'NO_STAR_CHIPS',
  'INSUFFICIENT_STAR_CHIPS',
  'NO_COINS',

  // O alvo, so no Duelo Rapido (docs/09 secao 2.2).
  'TARGET_UNKNOWN',
  'TARGET_OFFLINE',
  'TARGET_BUSY',
  'ROOM_UNAVAILABLE',

  // O modo, quando ele existe e nao aceita agora.
  'LOCKED',
  'TRACK_CLOSED',
  'TRACK_COMPLETED',
  'RACE_CLOSED',
  'RACE_COMPLETED',
  /**
   * A trilha ou a corrida com restricao de deck sem deck do bot valido naquele degrau (ADR 0041).
   * A publicacao ja exige a cobertura; isto e a rede da carta aposentada depois.
   */
  'BOT_DECKS_UNAVAILABLE',
  'NOT_FORMING',
  'CHAMPIONSHIP_FULL',
  'CODE_UNAVAILABLE',

  /**
   * Os tres que nascem no CLIENTE, e nao no servidor.
   *
   * Eles estao na mesma lista de proposito. Sem eles, a tela teria um tipo para o que o servidor
   * diz e outro para o que acontece quando ele nao diz nada — e o mapa de textos deixaria de ser
   * um, que e a propriedade inteira que esta lista compra.
   *
   * `UNKNOWN` e o destino do codigo que chega pela rede e nao esta aqui. O servidor andar antes do
   * cliente deixou de ser hipotese, e um codigo desconhecido nao pode virar dialogo em branco.
   */
  'NETWORK',
  'INVALID_RESPONSE',
  'UNKNOWN',
] as const

export type DuelGateRefusal = (typeof DUEL_GATE_REFUSALS)[number]

const KNOWN: ReadonlySet<string> = new Set(DUEL_GATE_REFUSALS)

/**
 * A borda da rede. O que passa e `DuelGateRefusal`; o que nao passa vira `UNKNOWN` no chamador.
 *
 * E o unico ponto do desenho que o compilador nao cobre — os tres elos da cadeia valem sobre o
 * codigo escrito, e este vale sobre o que chegou.
 */
export function isDuelGateRefusal(value: string): value is DuelGateRefusal {
  return KNOWN.has(value)
}
